import { type CallService } from '../../interface';
import { type Plugin } from '../../../pluggableService/pluggableService';
import { TimerObserver } from '../../../../libs/timerObserver';
import { getLogger } from '../../../../libs/logger';
import { timerKeys } from '../../../../shared/constants';
import {
  session,
  SessionEventType,
  SessionConnectionStatus,
} from '../../../../webrtc/session';

export enum CallReconnectionStep {
  ReconnectionStarted = 'reconnectionStarted',
  ReconnectionCompleted = 'reconnectionCompleted',
  /**
   * Disconnect until the reconnection process is completed
   */
  DisconnectedDuringReconnection = 'disconnectedDuringReconnection',
}

/**
 * CallService TimerPlugin
 *
 * CallReconnectingTimer
 * It listens for changes in connection status
 * and starts or finishes the timer if there is a reconnection to the call
 *
 * Implements the Plugin interface for CallService
 */
export class TimerPlugin implements Plugin<CallService, object> {
  name = 'TimerPlugin';

  private readonly callReconnectingTimer:
    | TimerObserver<CallReconnectionStep>
    | undefined;

  private isCallReconnectingTimerStarted = false;

  private readonly unsubscribes: (() => void)[] = [];

  constructor() {
    this.callReconnectingTimer = new TimerObserver<CallReconnectionStep>(
      timerKeys.callReconnecting,
      getLogger('Call/TimerPlugin/CallReconnecting'),
    );
  }

  apply<T extends CallService>(callService: T): T {
    this.unsubscribes.push(
      session.on([SessionEventType.ConnectionStatusChanged], (event) => {
        if (event.data?.connectionStatus) {
          this.setCallReconnectingTimer(event.data.connectionStatus);
        }
      }),
    );

    return callService;
  }

  private setCallReconnectingTimer(
    connectionStatus: SessionConnectionStatus,
  ): void {
    switch (connectionStatus) {
      case SessionConnectionStatus.Reconnecting: {
        this.startCallReconnectingTimer();
        break;
      }

      case SessionConnectionStatus.Connected: {
        this.finishCallReconnectingTimer(
          CallReconnectionStep.ReconnectionCompleted,
        );
        break;
      }

      case SessionConnectionStatus.Disconnected: {
        this.finishCallReconnectingTimer(
          CallReconnectionStep.DisconnectedDuringReconnection,
        );
        break;
      }

      default:
    }
  }

  private startCallReconnectingTimer(): void {
    this.callReconnectingTimer?.start(CallReconnectionStep.ReconnectionStarted);
    this.isCallReconnectingTimerStarted = true;
  }

  private finishCallReconnectingTimer(
    step:
      | CallReconnectionStep.ReconnectionCompleted
      | CallReconnectionStep.DisconnectedDuringReconnection,
  ): void {
    if (this.isCallReconnectingTimerStarted) {
      this.callReconnectingTimer?.finish(step);
      this.isCallReconnectingTimerStarted = false;
    }
  }

  destroy(): void {
    for (const unsubscribe of this.unsubscribes) {
      unsubscribe();
    }
  }
}
